import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { FileSignedUrl, TMDXFile } from '@/api/apis/files';
import { FileMeta } from '@/hooks/useUploadFile';
import { updateCaseCache } from '../case';

export enum FileType {
  Facility = 'Facility',
  Case = 'Case',
  Resource = 'Resource',
  Customer = 'Customer',
  OpoContact = 'OpoContact',
  Opo = 'Opo',
  Offer = 'Offer',
}

const mutationByFileType = {
  [FileType.Case]: api.files.getCaseFileSignedUrl,
  [FileType.Facility]: api.files.getFacilityFileSignedUrl,
  [FileType.Customer]: api.files.getCustomerFileSignedUrl,
  [FileType.Resource]: api.files.getResourceFileSignedUrl,
  [FileType.OpoContact]: api.files.getOpoContactPhotoFileSignedUrl,
  [FileType.Opo]: api.files.getOpoFileSignedUrl,
  [FileType.Offer]: api.files.getOfferFileSignedUrl,
};

const getSignedUrls = async ({
  filesMeta,
  fileType,
}: {
  filesMeta: FileMeta[];
  fileType: FileType;
}): Promise<PromiseSettledResult<FileSignedUrl>[]> => {
  const signedUrls = filesMeta.map((fileMeta: FileMeta) => {
    const mutation = mutationByFileType[fileType];
    return mutation(fileMeta);
  });

  return Promise.allSettled(signedUrls);
};

export const useGetFileSignedUrlsMutation = () => {
  return useMutation({
    mutationFn: ({ files, fileType }: { files: FileMeta[]; fileType: FileType }) =>
      getSignedUrls({ filesMeta: files, fileType }),
    onSuccess: (response) => {
      const errorResponse = response.find((result) => result.status === 'rejected');
      if (errorResponse) {
        throw errorResponse.reason;
      }
    },
  });
};

export const useDeleteCaseFileMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, fileId }: { caseId: string; fileId: string }) => api.files.caseFileRemove(caseId, fileId),
    onSuccess: updateCaseCache,
  });
};

export const useAddCaseFilesMutation = () => {
  return useMutation({
    mutationFn: ({ caseId, files }: { caseId: string; files: TMDXFile[] }) =>
      api.files.caseFileAttach(caseId, { files }),
    onSuccess: updateCaseCache,
  });
};

export const useReallocationFilesTransfer = () => {
  return useMutation({
    mutationFn: ({ caseId }: { caseId: string }) => api.files.reallocationCaseFilesTransfer(caseId),
    onSuccess: updateCaseCache,
  });
};
